import { identity } from 'lodash'

import { batch } from './undo'

import {
  ADD_LINE,
  ADD_LINE_WITH_TITLE,
  ADD_LINES_FROM_TEMPLATE,
  EDIT_LINE,
  EDIT_LINE_TITLE,
  EDIT_LINE_COLOR,
  REORDER_LINES,
  DELETE_LINE,
  EXPAND_LINE,
  COLLAPSE_LINE,
  LOAD_LINES,
  DUPLICATE_LINE,
  MOVE_LINE,
  PIN_PLOTLINE,
  UNPIN_PLOTLINE,
} from '../constants/ActionTypes'
import { reorderList } from '../helpers/lists'
import selectors from '../selectors'

const { currentTimelineSelector, pinnedPlotlinesSelector, sortedLinesByBookSelector } =
  selectors(identity)

/**
 * @typedef {import('../index').Line} Line
 * @typedef {import('../index').PlotlineTemplate} PlotlineTemplate
 * @typedef {import('../index').PlotlineTemplateData} PlotlineTemplateData
 * @typedef {import('../index').PlotlineCustomTemplate} PlotlineCustomTemplate
 * @typedef {import('../index').PlotlineCustomTemplateData} PlotlineCustomTemplateData
 * @typedef {import('../index').Dispatch} Dispatch
 * @typedef {import('../index').GetState} GetState
 * @typedef {import('../index').PlottrFileBeatsEntry} PlottrFileBeatsEntry
 */

/**
 * @typedef {{ type: 'ADD_LINES_FROM_TEMPLATE', templateData: PlotlineTemplateData | PlotlineCustomTemplateData, id: String, lines: Array<Line>, nextCardId: number, nextLineId: number, cardToBeatIdMap: Record<number, number>, newTree: PlottrFileBeatsEntry, createdNewBeats: boolean, bookId: 'series' | number }} AddLinesFromTemplateAction
 * @typedef {{ type: 'MOVE_LINE', id: number, destinationBookId: number | 'series', cardToBeatIdMapping: Record<number, number> }} MoveLineAction
 * @typedef {{ type: 'DUPLICATE_LINE', id: number, position: number, newLineId: number }} DuplicateLineAction
 */

/**
 * @typedef {ReturnType<typeof addLine> | ReturnType<typeof addLineWithTitle> | ReturnType<typeof editLine> | ReturnType<typeof editLineTitle> | ReturnType<typeof editLineColor> | ReturnType<typeof _reorderLines> | ReturnType<typeof _unpinPlotline> | ReturnType<typeof _pinPlotline> | ReturnType<typeof deleteLine> | ReturnType<typeof expandLine> | ReturnType<typeof collapseLine> | ReturnType<typeof load> | ReturnType<typeof pinMovedLine> | AddLinesFromTemplateAction | MoveLineAction | DuplicateLineAction} LinesActions
 */

// N.B. if one does not supply a book ID, then it is assumed that the
// action refers to the broadest scope possible, i.e. the series of
// books.

/**
 * @param {number | 'series'} bookId
 * @return {{ type: 'ADD_LINE', bookId: number | 'series' }}
 */
export function addLine(bookId) {
  return { type: ADD_LINE, bookId }
}

/**
 * @param {String} title
 * @param {number | 'series'} bookId
 * @return {{ type: 'ADD_LINE_WITH_TITLE', title: String, bookId: number | 'series' }}
 */
export function addLineWithTitle(title, bookId) {
  return { type: ADD_LINE_WITH_TITLE, title, bookId }
}

/**
 * @param {PlotlineTemplateData | PlotlineCustomTemplateData} templateData
 * @param {String} id
 * @param {Array<Line>} lines
 * @return {{ type: 'ADD_LINES_FROM_TEMPLATE', templateData: PlotlineTemplateData | PlotlineCustomTemplateData, id: String, lines: Array<Line> }}
 */
export function addLinesFromTemplate(templateData, id, lines) {
  return { type: ADD_LINES_FROM_TEMPLATE, templateData, id, lines }
}

/**
 * @param {number} id
 * @param {String} title
 * @param {String} color
 * @return {{ type: 'EDIT_LINE', id: number, title: String, color: String }}
 */
export function editLine(id, title, color) {
  return { type: EDIT_LINE, id, title, color }
}

/**
 * @param {number} id
 * @param {String} title
 * @return {{ type: 'EDIT_LINE_TITLE', id: number, title: String }}
 */
export function editLineTitle(id, title) {
  return { type: EDIT_LINE_TITLE, id, title }
}

/**
 * @param {number} id
 * @param {String} color
 * @return {{ type: 'EDIT_LINE_COLOR', id: number, color: String }}
 */
export function editLineColor(id, color) {
  return { type: EDIT_LINE_COLOR, id, color }
}

/**
 * @param {Array<Line>} lines
 * @param {'series' | number} bookId
 * @return {{ type: REORDER_LINES, lines: Array<Line>, bookId: 'series' | number }}
 */
export const _reorderLines = (lines, bookId) => {
  return { type: REORDER_LINES, lines, bookId }
}

/**
 * Reorder lines by their absolute (i.e. displayed) position in the
 * app.  Displayed position takes into account whether lines are
 * pinned or unpinned.
 *
 * @param {number} droppedPosition
 * @param {number} originalPosition
 * @return {function(Dispatch, GetState): Void}
 */
export const reorderLines = (droppedPosition, originalPosition) => (dispatch, getState) => {
  const state = getState()
  const lines = sortedLinesByBookSelector(state)
  const bookId = currentTimelineSelector(state)

  const droppedLine = lines[originalPosition]
  const targetLine = lines[droppedPosition]

  if (droppedLine.isPinned === targetLine.isPinned) {
    const reorderedLines = reorderList(droppedPosition, originalPosition, lines)
    dispatch(_reorderLines(reorderedLines, bookId))
  }
}

/**
 * @param {number} lineId
 * @param {number | 'series'} bookId
 * @param {number} totalPinnedPlotlines
 * @return {{ type: UNPIN_PLOTLINE, lineId: number, bookId: number | 'series', totalPinnedPlotlines: number }}
 */
export const _unpinPlotline = (lineId, bookId, totalPinnedPlotlines) => {
  return {
    type: UNPIN_PLOTLINE,
    lineId,
    bookId,
    totalPinnedPlotlines,
  }
}

/**
 * @param {number} lineId
 * @param {number | 'series'} bookId
 * @param {Array<Line> | Null} reorderedLines
 * @param {number} totalPinnedPlotlines
 * @return {{ type: PIN_PLOTLINE, lineId: number, bookId: number | 'series', reorderedLines: Array<Line> | Null, totalPinnedPlotlines: number }}
 */
export const _pinPlotline = (lineId, bookId, reorderedLines, totalPinnedPlotlines) => {
  return {
    type: PIN_PLOTLINE,
    lineId,
    bookId,
    reorderedLines,
    totalPinnedPlotlines,
  }
}

/**
 * @param {Line} line
 * @return {function(Dispatch, GetState): Void}
 */
export const togglePinPlotline = (line) => (dispatch, getState) => {
  const state = getState()
  const pinnedPlotlines = pinnedPlotlinesSelector(state)
  const bookId = currentTimelineSelector(state)

  if (!isNaN(line?.id)) {
    if (line?.isPinned) {
      const totalPinnedPlotlines = Math.max(0, pinnedPlotlines - 1)
      return dispatch(_unpinPlotline(line.id, bookId, totalPinnedPlotlines))
    } else {
      const totalPinnedPlotlines = Math.max(1, pinnedPlotlines + 1)
      return dispatch(_pinPlotline(line.id, bookId, null, totalPinnedPlotlines))
    }
  }
  return false
}

/**
 * @param {number} id
 * @param {number | 'series' | Null} bookId
 * @param {boolean | Null} isPinned
 * @return {{ type: 'DELETE_LINE', id: number, bookId: 'series' | number | Null, isPinned: boolean | Null }}
 */
export const deleteLine = (id, bookId, isPinned) => {
  return { type: DELETE_LINE, id, bookId, isPinned }
}

/**
 * @param {number} id
 * @return {{ type: 'EXPAND_LINE', id: number }}
 */
export function expandLine(id) {
  return { type: EXPAND_LINE, id }
}

/**
 * @param {number} id
 * @return {{ type: 'COLLAPSE_LINE', id: number }}
 */
export function collapseLine(id) {
  return { type: COLLAPSE_LINE, id }
}

/**
 * @param {number} id
 * @param {number} position
 * @return {function(Dispatch, GetState): Void}
 */
const pinDuplicatedPlotline = (id, position) => (dispatch, getState) => {
  const state = getState()
  const pinnedPlotlines = pinnedPlotlinesSelector(state)
  const lines = sortedLinesByBookSelector(state)
  const bookId = currentTimelineSelector(state)

  const selectedLine = lines.find((l) => l.id === id)
  const duplicatedLine = lines.find(
    (l) => l.title == selectedLine?.title && l?.isPinned && selectedLine?.isPinned
  )
  if (duplicatedLine) {
    const reorderedLines = reorderList(pinnedPlotlines, position, lines)
    const totalPinnedPlotlines = Math.max(1, pinnedPlotlines + 1)
    dispatch(_pinPlotline(id, bookId, reorderedLines, totalPinnedPlotlines))
  }
}

/**
 * @param {number} id
 * @param {number} position
 * @return {{ type: 'DUPLICATE_LINE', id: number, position: number }}
 */
export const _duplicateLine = (id, position) => {
  return { type: DUPLICATE_LINE, id, position }
}

/**
 * @param {number} id
 * @param {number} position
 * @return {function(Dispatch, GetState): Void}
 */
export const duplicateLine = (id, position) => (dispatch, getState) => {
  const state = getState()
  const lines = sortedLinesByBookSelector(state)

  batch('Duplicate Line', () => {
    dispatch(
      // NOTE(ed): we add attributes in the root reducer
      //
      // @ts-ignore
      _duplicateLine(id, position)
    )

    const selectedLine = lines.find((l) => l.id === id)
    if (selectedLine?.isPinned) {
      pinDuplicatedPlotline(id, position)(dispatch, getState)
    }
  })(dispatch)
}

/**
 * @param {boolean} patching
 * @param {Array<Line>} lines
 * @return {{ type: 'LOAD_LINES', patching: boolean, lines: Array<Line> }}
 */
export function load(patching, lines) {
  return { type: LOAD_LINES, patching, lines }
}

/**
 * @param {number} id
 * @param {number | 'series'} destinationBookId
 * @param {Array<Line>} reorderedLines
 * @param {number} totalPinnedPlotlines
 * @return {{ type: 'PIN_PLOTLINE', lineId: number, bookId: number | 'series', lines: Array<Line>, totalPinnedPlotlines: number }}
 */
export const pinMovedLine = (id, destinationBookId, reorderedLines, totalPinnedPlotlines) => {
  return {
    type: PIN_PLOTLINE,
    lineId: id,
    lines: reorderedLines,
    bookId: destinationBookId,
    totalPinnedPlotlines,
  }
}

/**
 * @param {number} id
 * @param {number | 'series'} destinationBookId
 * @return {{ type: 'MOVE_LINE', id: number, destinationBookId: number | 'series' }}
 */
export const moveLine = (id, destinationBookId) => {
  return { type: MOVE_LINE, id, destinationBookId }
}
